import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShipperCreditProfile, useShipperCreditProfile } from "@/hooks/useShipperCreditProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, Check, ChevronDown, CircleAlert, CircleDashed, Loader2, PenLine } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  companyId: string;
  onSaved?: () => void;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const BUSINESS_TYPES = ["Incorporated", "Partnership", "Proprietorship", "LLC", "Other"];
const PAYMENT_METHODS = ["ach", "check", "wire", "card"];

type Key = keyof ShipperCreditProfile;

interface SectionDef {
  id: string;
  letter: string;
  title: string;
  optional?: boolean;
  required: Key[];
}

const SECTIONS: SectionDef[] = [
  { id: "credit",   letter: "A", title: "Credit Request",         optional: true,  required: [] },
  { id: "identity", letter: "B", title: "Company Identity",       required: ["legal_name","ein"] },
  { id: "physical", letter: "C", title: "Physical Address",       required: ["physical_address_line1","physical_city","physical_state","physical_postal_code"] },
  { id: "contact",  letter: "D", title: "Company Contact",        required: ["company_phone","company_email"] },
  { id: "business", letter: "E", title: "Business Info",          required: ["business_structure","business_started_on","industry"] },
  { id: "billing",  letter: "F", title: "Billing Preferences",    optional: true, required: [] },
  { id: "ap",       letter: "G", title: "Payable Contact",        required: ["ap_contact_name","ap_contact_email","ap_contact_phone"] },
  { id: "signer",   letter: "H", title: "Authorized Signer",      required: ["signer_name","signer_title","signer_email","signer_phone","signer_signature_text"] },
  { id: "terms",    letter: "I", title: "Payment Terms & Volume", optional: true, required: [] },
  { id: "bank",     letter: "J", title: "Bank Reference",         optional: true, required: [] },
  { id: "refs",     letter: "K", title: "Trade References (3)",   optional: true, required: [] },
  
  { id: "notes",    letter: "M", title: "Notes for Brokers",      optional: true, required: [] },
];

function hasValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "number") return !Number.isNaN(v);
  return true;
}

function sectionStatus(
  sec: SectionDef,
  form: Partial<ShipperCreditProfile>,
): "complete" | "in_progress" | "not_started" {
  if (sec.optional) {
    // optional sections count as complete if any field across the entire form for this section has data?
    // Simpler: optional sections always "complete" so progress reflects required sections truly done.
    // But we want a meaningful status — treat optional as complete only when at least one of its known fields filled,
    // else "not_started". Section "in_progress" doesn't really apply.
    return "complete";
  }
  const filled = sec.required.filter((k) => hasValue(form[k])).length;
  if (filled === 0) return "not_started";
  if (filled < sec.required.length) return "in_progress";
  return "complete";
}

// ---------- input helpers ----------
function formatEin(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}
function isValidEin(v: string | null | undefined): boolean {
  if (!v) return true;
  return /^\d{2}-\d{7}$/.test(v);
}
function formatPhone(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function isValidPhone(v: string | null | undefined): boolean {
  if (!v) return true;
  return v.replace(/\D/g, "").length === 10;
}
function isValidEmail(v: string | null | undefined): boolean {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function yearsSince(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  try {
    const d = parseISO(dateStr);
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return null;
    return Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000));
  } catch {
    return null;
  }
}

// MM/DD/YYYY helpers for the business-started field.
function formatDateMask(s: string): string {
  const d = s.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}
function mmddyyyyToIso(s: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const mm = +m[1], dd = +m[2], yy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yy < 1900) return null;
  const dt = new Date(yy, mm - 1, dd);
  if (dt.getFullYear() !== yy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  if (dt.getTime() > Date.now()) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
}
function isoToMmddyyyy(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}


// ---------- field UI primitives ----------
function Field({
  label,
  required,
  error,
  children,
  htmlFor,
  hint,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
  htmlFor?: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs flex items-center gap-1">
        {label}
        {required ? <span className="text-destructive">*</span> : <span className="text-muted-foreground/70">(optional)</span>}
      </Label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
      {error && <p className="text-[10px] text-destructive">{error}</p>}
    </div>
  );
}

function StatusPill({ status }: { status: ReturnType<typeof sectionStatus> }) {
  if (status === "complete") {
    return (
      <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400 gap-1">
        <Check className="h-3 w-3" /> Complete
      </Badge>
    );
  }
  if (status === "in_progress") {
    return (
      <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400 gap-1">
        <CircleAlert className="h-3 w-3" /> In progress
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground gap-1">
      <CircleDashed className="h-3 w-3" /> Not started
    </Badge>
  );
}

// ---------- modal ----------
export function CompleteCreditProfileModal({ open, onOpenChange, companyId, onSaved }: Props) {
  const { profile, upsert, loading } = useShipperCreditProfile(companyId);
  const [form, setForm] = useState<Partial<ShipperCreditProfile>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [startedInput, setStartedInput] = useState<string>("");
  const [startedError, setStartedError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setForm(profile);
      setStartedInput(isoToMmddyyyy(profile.business_started_on));
    }
  }, [profile]);


  // First open: expand first incomplete required section
  useEffect(() => {
    if (!open) return;
    setOpenSections((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const first = SECTIONS.find((s) => !s.optional && sectionStatus(s, form) !== "complete");
      return first ? { [first.id]: true } : { identity: true };
    });
  }, [open, form]);

  const set = <K extends Key>(k: K, v: ShipperCreditProfile[K] | null) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Auto-derive years_in_business
  useEffect(() => {
    const y = yearsSince(form.business_started_on as string | null | undefined);
    if (y !== null && form.years_in_business !== y) {
      setForm((f) => ({ ...f, years_in_business: y }));
    }
  }, [form.business_started_on]); // eslint-disable-line react-hooks/exhaustive-deps

  const statuses = useMemo(
    () => Object.fromEntries(SECTIONS.map((s) => [s.id, sectionStatus(s, form)])),
    [form],
  );
  const completeCount = SECTIONS.filter((s) => statuses[s.id] === "complete").length;

  const einError = !isValidEin(form.ein) ? "Format: XX-XXXXXXX" : null;
  const companyEmailError = !isValidEmail(form.company_email) ? "Invalid email" : null;
  const apEmailError = !isValidEmail(form.ap_contact_email) ? "Invalid email" : null;
  const signerEmailError = !isValidEmail(form.signer_email) ? "Invalid email" : null;
  const companyPhoneError = !isValidPhone(form.company_phone) ? "10-digit US phone" : null;
  const apPhoneError = !isValidPhone(form.ap_contact_phone) ? "10-digit US phone" : null;
  const signerPhoneError = !isValidPhone(form.signer_phone) ? "10-digit US phone" : null;

  const handleSave = async () => {
    setSaving(true);
    // Strip auto-derived metadata that hook would set itself
    const payload: Partial<ShipperCreditProfile> = { ...form };
    const { error } = await upsert(payload);
    setSaving(false);
    if (error) {
      toast.error(typeof error === "string" ? error : (error as any).message);
      return;
    }
    toast.success("Credit profile saved");
    onSaved?.();
  };

  const toggle = (id: string) =>
    setOpenSections((p) => ({ ...p, [id]: !p[id] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle>Credit Profile</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Shared only with brokers you award loads to. Partial saves are allowed.
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">{completeCount} of {SECTIONS.length} sections complete</p>
              <div className="mt-1 h-1.5 w-48 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-[#02857E] transition-all"
                  style={{ width: `${(completeCount / SECTIONS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {SECTIONS.map((sec) => {
                const status = statuses[sec.id];
                const isOpen = !!openSections[sec.id];
                return (
                  <Collapsible
                    key={sec.id}
                    open={isOpen}
                    onOpenChange={() => toggle(sec.id)}
                    className="rounded-lg border bg-card"
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-mono text-muted-foreground w-5">{sec.letter}.</span>
                          <span className="text-sm font-medium truncate">{sec.title}</span>
                          {sec.optional && (
                            <span className="text-[10px] text-muted-foreground/70">(optional)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusPill status={status} />
                          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                        </div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-4 pb-4 pt-2 border-t">
                      {sec.id === "credit" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field
                            label="Requested Credit Limit Amount"
                            hint="How much credit you'd like brokers to extend (USD)."
                          >
                            <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                              <Input
                                type="number"
                                inputMode="decimal"
                                min={0}
                                step="1000"
                                className="pl-6"
                                placeholder="25000"
                                value={form.credit_limit_request_usd ?? ""}
                                onChange={(e) => set("credit_limit_request_usd", e.target.value === "" ? null : Number(e.target.value))}
                              />
                            </div>
                          </Field>
                          <Field label="Sales rep / broker contact">
                            <Input
                              value={form.sales_rep_name ?? ""}
                              onChange={(e) => set("sales_rep_name", e.target.value)}
                            />
                          </Field>
                        </div>
                      )}


                      {sec.id === "identity" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Legal name" required>
                            <Input value={form.legal_name ?? ""} onChange={(e) => set("legal_name", e.target.value)} />
                          </Field>
                          <Field label="Trade name / DBA">
                            <Input value={form.dba ?? ""} onChange={(e) => set("dba", e.target.value)} />
                          </Field>
                          <Field label="EIN" required error={einError} hint="Format: XX-XXXXXXX">
                            <Input
                              value={form.ein ?? ""}
                              onChange={(e) => set("ein", formatEin(e.target.value))}
                              placeholder="12-3456789"
                            />
                          </Field>
                          <Field label="DUNS number">
                            <Input value={form.duns_number ?? ""} onChange={(e) => set("duns_number", e.target.value)} />
                          </Field>
                        </div>
                      )}

                      {sec.id === "physical" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Address line 1" required>
                            <Input value={form.physical_address_line1 ?? ""} onChange={(e) => set("physical_address_line1", e.target.value)} />
                          </Field>
                          <Field label="Address line 2">
                            <Input value={form.physical_address_line2 ?? ""} onChange={(e) => set("physical_address_line2", e.target.value)} />
                          </Field>
                          <Field label="City" required>
                            <Input value={form.physical_city ?? ""} onChange={(e) => set("physical_city", e.target.value)} />
                          </Field>
                          <Field label="State" required>
                            <Select value={form.physical_state ?? ""} onValueChange={(v) => set("physical_state", v)}>
                              <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="ZIP" required>
                            <Input value={form.physical_postal_code ?? ""} onChange={(e) => set("physical_postal_code", e.target.value)} />
                          </Field>
                          <Field label="Country">
                            <Input value={form.physical_country ?? "US"} disabled />
                          </Field>
                        </div>
                      )}

                      {sec.id === "contact" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Phone" required error={companyPhoneError}>
                            <Input
                              value={form.company_phone ?? ""}
                              onChange={(e) => set("company_phone", formatPhone(e.target.value))}
                              placeholder="(555) 123-4567"
                            />
                          </Field>
                          <Field label="Email" required error={companyEmailError}>
                            <Input
                              type="email"
                              value={form.company_email ?? ""}
                              onChange={(e) => set("company_email", e.target.value)}
                            />
                          </Field>
                          <Field label="Website">
                            <Input value={form.company_website ?? ""} onChange={(e) => set("company_website", e.target.value)} placeholder="https://" />
                          </Field>
                          <Field label="Fax">
                            <Input value={form.company_fax ?? ""} onChange={(e) => set("company_fax", formatPhone(e.target.value))} />
                          </Field>
                        </div>
                      )}

                      {sec.id === "business" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Business type" required>
                            <Select value={form.business_structure ?? ""} onValueChange={(v) => set("business_structure", v)}>
                              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                              <SelectContent>
                                {BUSINESS_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                {/* preserve legacy free-text */}
                                {form.business_structure && !BUSINESS_TYPES.includes(form.business_structure) && (
                                  <SelectItem value={form.business_structure}>{form.business_structure}</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="State of incorporation">
                            <Select value={form.state_of_incorporation ?? ""} onValueChange={(v) => set("state_of_incorporation", v)}>
                              <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field
                            label="Date business started"
                            required
                            hint="MM/DD/YYYY"
                            error={startedError}
                          >
                            <Input
                              inputMode="numeric"
                              placeholder="MM/DD/YYYY"
                              maxLength={10}
                              value={startedInput}
                              onChange={(e) => {
                                const masked = formatDateMask(e.target.value);
                                setStartedInput(masked);
                                if (masked.length === 10) {
                                  const iso = mmddyyyyToIso(masked);
                                  if (iso) {
                                    set("business_started_on", iso);
                                    setStartedError(null);
                                  } else {
                                    set("business_started_on", null);
                                    setStartedError("Enter a valid past date");
                                  }
                                } else {
                                  set("business_started_on", null);
                                  setStartedError(null);
                                }
                              }}
                              onBlur={() => {
                                if (startedInput && startedInput.length < 10) {
                                  setStartedError("Enter a valid past date");
                                }
                              }}
                            />
                          </Field>


                          <Field label="Years in business" hint="Auto-derived from start date">
                            <Input
                              type="number"
                              value={form.years_in_business ?? ""}
                              onChange={(e) => set("years_in_business", e.target.value === "" ? null : Number(e.target.value))}
                              disabled={!!form.business_started_on}
                            />
                          </Field>
                          <Field label="Industry" required>
                            <Input value={form.industry ?? ""} onChange={(e) => set("industry", e.target.value)} />
                          </Field>
                          <Field label="Commodities to be shipped">
                            <Input value={form.commodities_shipped ?? ""} onChange={(e) => set("commodities_shipped", e.target.value)} />
                          </Field>
                        </div>
                      )}

                      {sec.id === "billing" && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between rounded-md border p-3">
                            <div>
                              <Label className="text-sm">Billing address same as physical</Label>
                              <p className="text-xs text-muted-foreground">Turn off to enter a separate billing address.</p>
                            </div>
                            <Switch
                              checked={form.billing_same_as_physical !== false}
                              onCheckedChange={(v) => set("billing_same_as_physical", v)}
                            />
                          </div>

                          {form.billing_same_as_physical === false && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Field label="Billing address line 1">
                                <Input value={form.billing_address_line1 ?? ""} onChange={(e) => set("billing_address_line1", e.target.value)} />
                              </Field>
                              <Field label="Billing address line 2">
                                <Input value={form.billing_address_line2 ?? ""} onChange={(e) => set("billing_address_line2", e.target.value)} />
                              </Field>
                              <Field label="City">
                                <Input value={form.billing_city ?? ""} onChange={(e) => set("billing_city", e.target.value)} />
                              </Field>
                              <Field label="State">
                                <Select value={form.billing_state ?? ""} onValueChange={(v) => set("billing_state", v)}>
                                  <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </Field>
                              <Field label="ZIP">
                                <Input value={form.billing_postal_code ?? ""} onChange={(e) => set("billing_postal_code", e.target.value)} />
                              </Field>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex items-center justify-between rounded-md border p-3">
                              <Label className="text-sm">PO numbers required on invoices</Label>
                              <Switch
                                checked={!!form.requires_po_number}
                                onCheckedChange={(v) => set("requires_po_number", v)}
                              />
                            </div>
                            <div className="flex items-center justify-between rounded-md border p-3">
                              <Label className="text-sm">Supporting docs required on invoices</Label>
                              <Switch
                                checked={!!form.requires_invoice_docs}
                                onCheckedChange={(v) => set("requires_invoice_docs", v)}
                              />
                            </div>
                          </div>

                          {form.requires_invoice_docs && (
                            <Field label="Which documents are required?">
                              <Textarea
                                rows={2}
                                value={form.invoice_docs_notes ?? ""}
                                onChange={(e) => set("invoice_docs_notes", e.target.value)}
                              />
                            </Field>
                          )}

                          <Field label="Special billing requirements">
                            <Textarea
                              rows={2}
                              value={form.billing_special_requirements ?? ""}
                              onChange={(e) => set("billing_special_requirements", e.target.value)}
                            />
                          </Field>
                        </div>
                      )}

                      {sec.id === "ap" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="AP contact name" required>
                            <Input value={form.ap_contact_name ?? ""} onChange={(e) => set("ap_contact_name", e.target.value)} />
                          </Field>
                          <Field label="AP email" required error={apEmailError}>
                            <Input type="email" value={form.ap_contact_email ?? ""} onChange={(e) => set("ap_contact_email", e.target.value)} />
                          </Field>
                          <Field label="AP phone" required error={apPhoneError}>
                            <Input
                              value={form.ap_contact_phone ?? ""}
                              onChange={(e) => set("ap_contact_phone", formatPhone(e.target.value))}
                            />
                          </Field>
                        </div>
                      )}

                      {sec.id === "signer" && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Signer name" required>
                              <Input value={form.signer_name ?? ""} onChange={(e) => set("signer_name", e.target.value)} />
                            </Field>
                            <Field label="Signer title" required>
                              <Input value={form.signer_title ?? ""} onChange={(e) => set("signer_title", e.target.value)} />
                            </Field>
                            <Field label="Signer email" required error={signerEmailError}>
                              <Input type="email" value={form.signer_email ?? ""} onChange={(e) => set("signer_email", e.target.value)} />
                            </Field>
                            <Field label="Signer phone" required error={signerPhoneError}>
                              <Input
                                value={form.signer_phone ?? ""}
                                onChange={(e) => set("signer_phone", formatPhone(e.target.value))}
                              />
                            </Field>
                          </div>
                          <SignatureSubsection
                            companyId={companyId}
                            signerNameFallback={form.signer_name ?? ""}
                            currentText={form.signer_signature_text ?? null}
                            currentSavedAt={form.signer_signature_saved_at ?? null}
                            onSaved={(row) => {
                              setForm((f) => ({
                                ...f,
                                signer_signature_text: row.signer_signature_text,
                                signer_signature_saved_at: row.signer_signature_saved_at,
                                signer_signature_ip: row.signer_signature_ip,
                                signer_signature_user_agent: row.signer_signature_user_agent,
                              }));
                            }}
                          />
                        </div>
                      )}

                      {sec.id === "terms" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Payment terms (days)">
                            <Input
                              type="number"
                              value={form.payment_terms_days ?? ""}
                              onChange={(e) => set("payment_terms_days", e.target.value === "" ? null : Number(e.target.value))}
                            />
                          </Field>
                          <Field label="Preferred payment method">
                            <Select value={form.preferred_payment_method ?? ""} onValueChange={(v) => set("preferred_payment_method", v)}>
                              <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                              <SelectContent>
                                {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="Annual freight spend (USD)">
                            <Input
                              type="number"
                              value={form.annual_freight_spend_usd ?? ""}
                              onChange={(e) => set("annual_freight_spend_usd", e.target.value === "" ? null : Number(e.target.value))}
                            />
                          </Field>
                          <Field label="Avg loads / month">
                            <Input
                              type="number"
                              value={form.avg_loads_per_month ?? ""}
                              onChange={(e) => set("avg_loads_per_month", e.target.value === "" ? null : Number(e.target.value))}
                            />
                          </Field>
                          <div className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
                            <Label className="text-sm">Factoring-friendly</Label>
                            <Switch
                              checked={!!form.factoring_friendly}
                              onCheckedChange={(v) => set("factoring_friendly", v)}
                            />
                          </div>
                        </div>
                      )}

                      {sec.id === "bank" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field label="Bank name">
                            <Input value={form.bank_name ?? ""} onChange={(e) => set("bank_name", e.target.value)} />
                          </Field>
                          <Field label="Bank contact name">
                            <Input value={form.bank_contact_name ?? ""} onChange={(e) => set("bank_contact_name", e.target.value)} />
                          </Field>
                          <Field label="Bank phone">
                            <Input
                              value={form.bank_contact_phone ?? ""}
                              onChange={(e) => set("bank_contact_phone", formatPhone(e.target.value))}
                            />
                          </Field>
                          <Field label="Bank email">
                            <Input type="email" value={form.bank_contact_email ?? ""} onChange={(e) => set("bank_contact_email", e.target.value)} />
                          </Field>
                          <Field label="Account (last 4 only)" hint="Never store the full account number">
                            <Input
                              maxLength={4}
                              value={form.bank_account_last4 ?? ""}
                              onChange={(e) => set("bank_account_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            />
                          </Field>
                          <Field label="Routing (last 4 only)">
                            <Input
                              maxLength={4}
                              value={form.bank_routing_last4 ?? ""}
                              onChange={(e) => set("bank_routing_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            />
                          </Field>
                        </div>
                      )}

                      {sec.id === "refs" && (
                        <div className="space-y-4">
                          {[1, 2, 3].map((n) => (
                            <div key={n} className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">Trade reference {n}</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Company">
                                  <Input
                                    value={(form as any)[`trade_ref_${n}_company`] ?? ""}
                                    onChange={(e) => set(`trade_ref_${n}_company` as Key, e.target.value as any)}
                                  />
                                </Field>
                                <Field label="Contact">
                                  <Input
                                    value={(form as any)[`trade_ref_${n}_contact`] ?? ""}
                                    onChange={(e) => set(`trade_ref_${n}_contact` as Key, e.target.value as any)}
                                  />
                                </Field>
                                <Field label="Phone">
                                  <Input
                                    value={(form as any)[`trade_ref_${n}_phone`] ?? ""}
                                    onChange={(e) => set(`trade_ref_${n}_phone` as Key, formatPhone(e.target.value) as any)}
                                  />
                                </Field>
                                <Field label="Email">
                                  <Input
                                    type="email"
                                    value={(form as any)[`trade_ref_${n}_email`] ?? ""}
                                    onChange={(e) => set(`trade_ref_${n}_email` as Key, e.target.value as any)}
                                  />
                                </Field>
                                <Field label="Account #">
                                  <Input
                                    value={(form as any)[`trade_ref_${n}_account`] ?? ""}
                                    onChange={(e) => set(`trade_ref_${n}_account` as Key, e.target.value as any)}
                                  />
                                </Field>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}


                      {sec.id === "notes" && (
                        <Field label="Notes for brokers">
                          <Textarea
                            rows={3}
                            value={form.notes_for_brokers ?? ""}
                            onChange={(e) => set("notes_for_brokers", e.target.value)}
                          />
                        </Field>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky save bar */}
        <div className="border-t bg-background px-6 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Partial saves are allowed — required fields are recommended for broker auto-fill.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Close
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#02857E] hover:bg-[#02857E]/90 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save credit profile
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Signature subsection (Phase 3) ----------
interface SignatureRow {
  signer_signature_text: string | null;
  signer_signature_saved_at: string | null;
  signer_signature_ip: string | null;
  signer_signature_user_agent: string | null;
}

function SignatureSubsection({
  companyId,
  signerNameFallback,
  currentText,
  currentSavedAt,
  onSaved,
}: {
  companyId: string;
  signerNameFallback: string;
  currentText: string | null;
  currentSavedAt: string | null;
  onSaved: (row: SignatureRow) => void;
}) {
  const [text, setText] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText((currentText && currentText.trim() !== "") ? currentText : (signerNameFallback ?? ""));
  }, [currentText, signerNameFallback]);

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Type your name to sign");
      return;
    }
    setSaving(true);
    try {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null;
      const { data, error } = await supabase.rpc("save_shipper_signature", {
        p_company_id: companyId,
        p_signature_text: trimmed,
        p_user_agent: ua,
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as SignatureRow | null;
      if (row) onSaved(row);
      toast.success("Signature saved");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Could not save signature: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const savedAtLabel = currentSavedAt
    ? (() => { try { return format(parseISO(currentSavedAt), "PP p"); } catch { return null; } })()
    : null;

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm flex items-center gap-1.5">
          <PenLine className="h-3.5 w-3.5" />
          Typed signature
          <span className="text-destructive">*</span>
        </Label>
        {savedAtLabel && (
          <span className="text-[11px] text-muted-foreground">
            Last signed {savedAtLabel}
          </span>
        )}
      </div>

      <Input
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 200))}
        placeholder="Type your full legal name"
        maxLength={200}
      />

      <div className="rounded border bg-background p-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Signature preview</p>
        <div className="font-signature text-[28px] leading-tight text-foreground min-h-[36px]">
          {text || <span className="text-muted-foreground/40 text-base font-sans italic">— preview —</span>}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-snug">
        By typing your name above and clicking Save Signature, you agree this constitutes your
        electronic signature under the federal E-SIGN Act, has the same legal effect as a
        handwritten signature, and authorizes LaneRunner to apply it to broker credit
        applications generated for your awarded loads. Your IP address, user agent, and
        timestamp will be recorded as part of the audit trail.
      </p>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={saving || !text.trim()}
          className="bg-[#02857E] hover:bg-[#02857E]/90 text-white"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {currentText ? "Update signature" : "Save signature"}
        </Button>
      </div>
    </div>
  );
}
